import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Phone, Mail, MapPin, Search, Smartphone, Compass, Rocket, Users, Heart, ChevronDown, ChevronUp, Star } from "lucide-react";
import simpletechLogoFooter from "@/assets/simpletech-logo-footer.png";
import { SiteHeader } from "@/components/SiteHeader";
import armandsHeadshot from "@/assets/armands-headshot.png";
import heroBg from "@/assets/hero-bg.jpg";
import missionBg from "@/assets/mission-bg.jpg";
import aboutHeroBg from "@/assets/about-hero-bg.png";
import mapsDotsBg from "@/assets/bg-maps-dots.jpg";
import dotsDecoration from "@/assets/dots-decoration.png";
import dotsDecorationRight from "@/assets/dots-decoration-right.png";
import logoBni from "@/assets/logos/bni.webp";
import logoAllstate from "@/assets/logos/allstate.webp";
import logoBenFranklin from "@/assets/logos/benjamin-franklin.webp";
import logoHharpp from "@/assets/logos/hharpp.webp";
import logoSimone from "@/assets/logos/simone-ravicz.webp";
import logoFarmers from "@/assets/logos/farmers.webp";
import { SITE_URL, canonical, seoCanonicalLink, seoOgUrl } from "@/lib/seo";

export const Route = createFileRoute("/about")({
  component: AboutPage,
  head: () => ({
    meta: [
      { title: "About SimpleTech — IT Support Company in Montclair, CA" },
      { name: "description", content: "Founded in 2016 by Armands Williams, SimpleTech provides managed IT, cybersecurity and VoIP to Inland Empire businesses, families and seniors." },
      { property: "og:title", content: "About SimpleTech — IT Support Company in Montclair, CA" },
      { property: "og:description", content: "Innovative, fast and friendly IT services provider — meet the team and our core values." },
      seoOgUrl("/about"),
    ],
    links: [
      seoCanonicalLink("/about"),
    ],
  }),
});

const principles = [
  { icon: Smartphone, title: "Integrity", desc: "We uphold honesty and transparency, fostering trust with our customers, partners, and team." },
  { icon: Compass, title: "Innovation", desc: "SimpleTech stays ahead of the curve by continuously advancing our platform with cutting-edge technologies and innovative features." },
  { icon: Rocket, title: "Excellence", desc: "We are committed to delivering top-quality service while continuously striving for improvement and excellence." },
  { icon: Users, title: "Customer-First Approach", desc: "Our customers are our priority. We are committed to understanding your needs and delivering tailored solutions." },
  { icon: Heart, title: "Collaboration", desc: "We uphold honesty and transparency, fostering trust with our customers, partners, and team." },
];

const coreValues = [
  {
    title: "Customers First",
    body: "At SimpleTech, we believe that the customers come first. We're here to help businesses, tech consumers and seniors stay up-to-date on the latest technology trends and solutions available for their needs.\n\nWith a focus on customer satisfaction and delivery of high-quality services, it's our mission to help you pursue your goals with confidence by connecting you to today's cutting-edge technology resources.",
  },
  { title: "Employee Appreciation", body: "Our team is the heart of SimpleTech. We invest in growth, recognition, and a culture where people thrive." },
  { title: "Community", body: "We believe in giving back, supporting local initiatives, and building stronger communities through technology." },
  { title: "Transparent Pricing", body: "No hidden fees. Clear, honest pricing so you always know exactly what you're paying for." },
  { title: "Passion", body: "We love what we do. Passion drives innovation, quality, and service that goes the extra mile." },
  { title: "Simplified Tech For All", body: "Technology should empower everyone. We make complex tech simple, accessible, and friendly." },
];

const partnerLogos = [
  { src: logoBenFranklin, alt: "Benjamin Franklin" },
  { src: logoAllstate, alt: "Allstate" },
  { src: logoBni, alt: "BNI" },
  { src: logoFarmers, alt: "Farmers" },
  { src: logoSimone, alt: "Simone Ravicz" },
  { src: logoHharpp, alt: "Hharpp" },
];

function AboutPage() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader active="about" />

      {/* Page hero */}
      <section className="relative min-h-[230px] md:min-h-[315px] text-white overflow-hidden flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-bottom"
          style={{ backgroundImage: `url(${aboutHeroBg})` }}
        />
        <div className="absolute inset-0 bg-[#0b0a3d]/40" />
        <div className="relative max-w-7xl mx-auto px-8 w-full py-12 flex items-center justify-between">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white">About</h1>
          <nav aria-label="Breadcrumb" className="hidden md:block">
            <ol className="flex items-center gap-2 text-white/80 text-sm font-medium tracking-wide">
              <li><Link to="/" className="hover:text-white transition-colors">HOME</Link></li>
              <li aria-hidden className="text-white/50">•</li>
              <li className="text-white">ABOUT</li>
            </ol>
          </nav>
        </div>
      </section>

      {/* About + headshot */}
      <section className="relative overflow-hidden">
        <img src={dotsDecoration} alt="" aria-hidden="true" width={256} height={256} className="absolute left-0 top-10 w-64 opacity-60 pointer-events-none" />
        <img src={dotsDecorationRight} alt="" aria-hidden="true" width={288} height={288} className="absolute right-0 bottom-0 w-72 opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-8 pt-20 grid md:grid-cols-2 gap-12 items-end relative">
          <div className="pb-20">
            <div className="text-sky-500 font-bold tracking-widest text-sm mb-4">ABOUT US</div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight">
              Innovative, Fast, And Friendly IT Services Provider.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              In 2016, SimpleTech was founded to make technology simple and reliable for small and medium businesses. We also support residential clients. Today, SimpleTech is a leading IT services provider, offering cloud solutions, advanced IT management, network services, and infrastructure solutions—ensuring technology works for everyone, not against them.
            </p>
            <Link
              to="/contact"
              className="inline-block mt-8 bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
            >
              GET STARTED
            </Link>
          </div>
          <div className="flex justify-center self-end">
            <img src={armandsHeadshot} alt="Armands Williams, SimpleTech founder and lead IT technician serving small businesses in Montclair, California" width={448} height={560} className="max-w-md w-full block ml-0 md:-ml-[25%]" />
          </div>
        </div>
      </section>

      {/* Partners strip */}
      <section className="py-12 border-b border-border overflow-hidden" style={{ backgroundColor: "#f8f8f8" }}>
        <div className="max-w-7xl mx-auto px-6 mb-8 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">
            Trusted by local businesses & professionals
          </p>
        </div>
        <div
          className="relative w-full overflow-hidden"
          style={{
            maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
            WebkitMaskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
          }}
        >
          <div className="flex w-max animate-marquee gap-16 items-center">
            {[...partnerLogos, ...partnerLogos].map((logo, i) => (
              <img
                key={`${logo.alt}-${i}`}
                src={logo.src}
                alt={logo.alt}
                className="h-14 md:h-16 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Core principles */}
      <section className="max-w-7xl mx-auto px-8 py-20">
        <div className="text-indigo-600 font-bold tracking-widest text-sm mb-3">WE SOLVE REAL PROBLEMS</div>
        <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Our Core Principles at SimpleTech</h2>
        <p className="mt-3 text-slate-600 max-w-3xl">
          At SimpleTech, our core values shape everything we do, reflecting our commitment to both our customers and our team.
        </p>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {principles.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="bg-slate-50 rounded-xl p-8 flex gap-5 items-start">
              <Icon className="w-8 h-8 text-indigo-600 shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{title}</h3>
                <p className="text-slate-600 leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Mission / Approach dark band */}
      <section className="relative bg-[#0b0f3d] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-60 bg-cover bg-center" style={{ backgroundImage: `url(${missionBg})` }} />
        <div className="relative max-w-7xl mx-auto px-8 py-20 grid md:grid-cols-2 gap-8">
          {[
            { tag: "OUR MISSION", title: "Why We're Different", body: "Integrity, experience, involvement, and dedication define who we are and set us apart. Our service-driven approach focuses on delivering results, reducing risks, and building lasting relationships with our clients and partners." },
            { tag: "SUPPORT AT ITS BEST", title: "Our Approach", body: "From onboarding to daily operations, SimpleTech provides a dedicated team to manage your IT infrastructure, support your users, and keep your systems running smoothly. Our proactive approach ensures fast response times, quick issue resolution, and ongoing communication—so you're always supported." },
          ].map((c) => (
            <div key={c.title} className="bg-white text-slate-900 rounded-lg p-10">
              <div className="text-sky-500 font-bold tracking-widest text-xs mb-3">{c.tag}</div>
              <h3 className="text-3xl font-extrabold mb-4">{c.title}</h3>
              <p className="text-slate-600 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Core Values accordion */}
      <section className="relative overflow-hidden bg-white">
        <img src={dotsDecoration} alt="" width={288} height={288} className="absolute left-0 top-10 w-72 opacity-70 pointer-events-none" />
        <img src={dotsDecorationRight} alt="" width={320} height={320} className="absolute right-0 bottom-0 w-80 opacity-70 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-8 py-20">
          <div className="text-sky-500 font-bold tracking-widest text-sm mb-3">WHO WE ARE</div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-10">Our Core Values</h2>
          <div className="space-y-3">
            {coreValues.map((v, i) => {
              const open = openIndex === i;
              return (
                <div key={v.title} className="bg-white border border-slate-200 rounded-md overflow-hidden">
                  <button
                    onClick={() => setOpenIndex(open ? -1 : i)}
                    className={`w-full flex items-center justify-between px-6 py-4 text-left font-bold ${open ? "bg-sky-50 text-sky-600" : "text-slate-900"}`}
                  >
                    <span>{v.title}</span>
                    {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  {open && (
                    <div className="px-6 pb-6 pt-2 text-slate-600 leading-relaxed whitespace-pre-line">
                      {v.body}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section
        className="relative py-24 md:py-32 overflow-hidden bg-slate-50 bg-no-repeat bg-top bg-cover"
        style={{ backgroundImage: `url(${mapsDotsBg})` }}
      >
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-sky-500 mb-3">From Our Clients</div>
            <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight text-[#0b0f3d]">Testimonials</h2>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <span className="font-bold text-[#0b0f3d]">Excellent</span>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <span className="text-sm text-slate-600"><strong className="text-[#0b0f3d]">5.0</strong> | 32 reviews</span>
              </div>
              <Link
                to="/contact"
                className="text-sm font-semibold px-4 py-2 rounded-md border border-slate-300 text-[#0b0f3d] hover:border-sky-400 hover:text-sky-500 transition-colors"
              >
                Write a review
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { name: "Christina Thompson", date: "22 July 2022", initial: "C", color: "bg-rose-400", text: "I used SimpleTech to set up my office phones for my second location. I was having a hard time setting them up myself and Armands made it seamless." },
                { name: "Camille Leon", date: "5 July 2022", initial: "C", color: "bg-amber-400", text: "Since hiring SimpleTech to handle social media posts for the Holistic Chamber of Commerce, it has been a game changer for our brand." },
                { name: "Shimmy Strzelczyk", date: "16 April 2026", initial: "S", color: "bg-violet-500", text: "We had a great experience with SimpleTech helping Shimmy Pest Control with our phone system and IT setup. Highly recommend!", featured: true },
                { name: "Malcolm Brown", date: "31 December 2025", initial: "M", color: "bg-fuchsia-500", text: "Armands was an incredible resource to have. He obviously knows what he's talking about and delivered every time." },
              ].map((r) => (
                <div
                  key={r.name}
                  className={`relative rounded-lg p-5 bg-white ${r.featured ? "shadow-xl ring-1 ring-slate-200" : "border border-slate-200"}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-full ${r.color} grid place-items-center text-white font-bold`}>
                      {r.initial}
                    </div>
                    <div>
                      <div className="font-bold text-sm text-[#0b0f3d] leading-tight">{r.name}</div>
                      <div className="text-xs text-slate-500">{r.date}</div>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">{r.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0b0f3d] text-white pt-16 pb-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 pb-12">
            <div className="flex flex-col items-start">
              <img src={simpletechLogoFooter} alt="SimpleTech logo — trusted IT support, cybersecurity, and managed services for Southern California businesses" width={160} height={53} className="w-40 h-auto mb-8" />
              <div className="flex items-center gap-3">
                {[
                  { label: "Facebook", initial: "f" },
                  { label: "Instagram", initial: "" },
                  { label: "LinkedIn", initial: "in" },
                  { label: "Google", initial: "G" },
                ].map((s) => (
                  <a key={s.label} href="#" aria-label={s.label} className="w-9 h-9 rounded-full bg-white text-[#0b0f3d] grid place-items-center text-sm font-bold hover:bg-[#28bcff] hover:text-white transition-colors">
                    {s.initial}
                  </a>
                ))}
              </div>
            </div>
            <div className="lg:col-span-2">
              <h3 className="text-2xl font-extrabold mb-6">Services</h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm text-slate-200">
                <Link to="/" hash="services" className="hover:text-[#28bcff]">Tech Support</Link>
                <Link to="/" hash="services" className="hover:text-[#28bcff]">Backup and Recovery</Link>
                <Link to="/" hash="services" className="hover:text-[#28bcff]">Managed IT</Link>
                <Link to="/" hash="services" className="hover:text-[#28bcff]">HIPPA Compliance</Link>
                <Link to="/" hash="services" className="hover:text-[#28bcff]">Cyber Security</Link>
                <Link to="/" hash="services" className="hover:text-[#28bcff]">Website Design</Link>
                <Link to="/" hash="services" className="hover:text-[#28bcff]">VoIP</Link>
                <Link to="/" hash="services" className="hover:text-[#28bcff]">Senior Tech Support</Link>
                <Link to="/" hash="services" className="hover:text-[#28bcff]">Software Solution</Link>
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-extrabold mb-6">Important</h3>
              <div className="flex flex-col gap-4 text-sm text-slate-200">
                <Link to="/" className="hover:text-[#28bcff]">Home</Link>
                <Link to="/about" className="hover:text-[#28bcff]">About</Link>
                <Link to="/services" className="hover:text-[#28bcff]">Services</Link>
                <Link to="/reviews" className="hover:text-[#28bcff]">Reviews</Link>
                <Link to="/contact" className="hover:text-[#28bcff]">Contact</Link>
              </div>
            </div>
            <div className="space-y-7 text-sm">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-white/10 grid place-items-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#28bcff]" />
                </div>
                <div>
                  <div className="text-lg font-bold mb-1">Call Us</div>
                  <a href="tel:9092559112" className="text-slate-200 hover:text-[#28bcff]">(909) 255-9112</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-white/10 grid place-items-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#28bcff]" />
                </div>
                <div>
                  <div className="text-lg font-bold mb-1">Email Us</div>
                  <a href="mailto:contact@simpletechhelper.com" className="text-slate-200 hover:text-[#28bcff] break-all">contact@simpletechhelper.com</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-white/10 grid place-items-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#28bcff]" />
                </div>
                <div>
                  <div className="text-lg font-bold mb-1">Mailing Address</div>
                  <div className="text-slate-200 leading-relaxed">
                    8880 N Benson Ave #121<br />
                    Montclair, CA 91763
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/15 pt-6 flex flex-col md:flex-row justify-between items-center gap-3 text-sm text-slate-300">
            <div className="flex items-center gap-3 flex-wrap">
              <Link to="/privacy-policy" className="hover:text-[#28bcff]">Privacy Policy</Link>
              <span className="text-white/20">|</span>
              <Link to="/terms-and-conditions" className="hover:text-[#28bcff]">Terms and Conditions</Link>
              <span className="text-white/20">|</span>
              <Link to="/accessibility-statement" className="hover:text-[#28bcff]">Accessibility</Link>
              <span className="text-white/20">|</span>
              <Link to="/sitemap" className="hover:text-[#28bcff]">Sitemap</Link>
            </div>
            <div>Copyright © {new Date().getFullYear()} SimpleTech. All Rights Reserved.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
